import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChevronLeft, ChevronRight, Car, CheckCircle2, XCircle, Wrench, ArrowLeftRight
} from "lucide-react";
import { formatDate } from "@/utils/dates";

const DAYS_TO_SHOW = 28;

function getDaysArray(startDate, count) {
  const days = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  return days;
}

function formatDateISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isDateInRange(dateStr, startStr, endStr) {
  // Normalize to YYYY-MM-DD only (strip time/timezone)
  const d = (dateStr || "").slice(0, 10);
  const s = (startStr || "").slice(0, 10);
  const e = (endStr || "").slice(0, 10);
  if (!d || !s || !e) return false;
  return d >= s && d <= e;
}

const dayLabels = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
const monthLabels = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

export default function CalendarPage() {
  const { axiosAuth } = useAuth();
  const [rentals, setRentals] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [selectedCell, setSelectedCell] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const api = axiosAuth();
      const [rentalsRes, vehiclesRes] = await Promise.all([api.get("/rentals"), api.get("/vehicles")]);
      setRentals(rentalsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const days = useMemo(() => getDaysArray(startDate, DAYS_TO_SHOW), [startDate]);
  const todayStr = formatDateISO(new Date());

  const rentalsByVehicle = useMemo(() => {
    const map = {};
    for (const r of rentals) {
      if (r.status === "annullato") continue;
      const vid = r.vehicle_id || r.vehicle?.id;
      if (!vid) continue;
      if (!map[vid]) map[vid] = [];
      map[vid].push(r);
    }
    return map;
  }, [rentals]);

  const getCellStatus = (vehicleId, dateStr, vehicleStatus) => {
    if (vehicleStatus === "manutenzione") return { type: "manutenzione", rental: null, rentals: [] };
    const vRentals = rentalsByVehicle[vehicleId] || [];
    const matching = [];
    for (const r of vRentals) {
      const start = (r.start_date || "").slice(0, 10);
      const end = (r.end_date || "").slice(0, 10);
      if (start && end && dateStr >= start && dateStr <= end) {
        matching.push(r);
      }
    }
    // Filter out completed for normal display, but keep for "cambio" detection
    const active = matching.filter(r => r.status !== "completato");
    if (matching.length >= 2 && active.length >= 1) {
      // At least 2 rentals on same day (one may be completed = rientro)
      return { type: "cambio", rental: active[0] || matching[0], rentals: matching };
    }
    if (active.length >= 2) {
      return { type: "cambio", rental: active[0], rentals: active };
    }
    if (active.length === 1) {
      const cellType = active[0].status === "prenotazione" ? "prenotazione" : "noleggiata";
      return { type: cellType, rental: active[0], rentals: active };
    }
    return { type: "disponibile", rental: null, rentals: [] };
  };

  const navigate = (direction) => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() + direction * 7);
    setStartDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setStartDate(today);
  };

  const stats = useMemo(() => {
    const rented = vehicles.filter(v => (rentalsByVehicle[v.id] || []).some(r => r.status === "attivo" && isDateInRange(todayStr, r.start_date, r.end_date))).length;
    const reserved = vehicles.filter(v => (rentalsByVehicle[v.id] || []).some(r => r.status === "prenotazione" && isDateInRange(todayStr, r.start_date, r.end_date))).length;
    const maintenance = vehicles.filter(v => v.status === "manutenzione").length;
    const available = vehicles.length - rented - reserved - maintenance;
    return { available: Math.max(available, 0), rented, reserved, maintenance, total: vehicles.length };
  }, [vehicles, rentalsByVehicle, todayStr]);

  const rangeLabel = `${days[0]?.getDate()} ${monthLabels[days[0]?.getMonth()]} - ${days[days.length - 1]?.getDate()} ${monthLabels[days[days.length - 1]?.getMonth()]} ${days[days.length - 1]?.getFullYear()}`;

  if (loading) {
    return (
      <div className="space-y-4" data-testid="calendar-page">
        <div className="h-12 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
        <div className="h-[500px] bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5" data-testid="calendar-page">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-outfit font-bold text-slate-900 dark:text-slate-100">Calendario Disponibilita</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">Panoramica veicoli - {rangeLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToToday} className="rounded-lg text-xs" data-testid="calendar-today-button">Oggi</Button>
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="rounded-lg h-8 w-8" data-testid="calendar-prev-button"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="icon" onClick={() => navigate(1)} className="rounded-lg h-8 w-8" data-testid="calendar-next-button"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>

        {/* Legend - vivid colors */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-emerald-500" />
            <span className="text-xs text-slate-600 dark:text-slate-300">Disponibile ({stats.available})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-rose-500" />
            <span className="text-xs text-slate-600 dark:text-slate-300">Noleggiata ({stats.rented})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-blue-500" />
            <span className="text-xs text-slate-600 dark:text-slate-300">Prenotazione ({stats.reserved})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-slate-500" />
            <span className="text-xs text-slate-600 dark:text-slate-300">Manutenzione ({stats.maintenance})</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-sm bg-orange-500" />
            <span className="text-xs text-slate-600 dark:text-slate-300">Cambio Cliente</span>
          </div>
          <div className="ml-auto text-xs text-slate-500 dark:text-slate-400">{stats.total} veicoli totali</div>
        </div>

        {/* Gantt Grid */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[700px]">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-20 bg-slate-100 dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 px-3 py-2 text-left min-w-[160px]">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">Veicolo</span>
                    </th>
                    {days.map((day) => {
                      const dateStr = formatDateISO(day);
                      const isToday = dateStr === todayStr;
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                      return (
                        <th key={dateStr} className={`border-b border-r border-slate-200 dark:border-slate-700 px-0.5 py-1 text-center min-w-[36px] ${isToday ? "bg-blue-100 dark:bg-blue-900/40" : isWeekend ? "bg-slate-100 dark:bg-slate-800" : "bg-white dark:bg-slate-900"}`}>
                          <div className="text-[9px] text-slate-400 dark:text-slate-500 uppercase">{dayLabels[day.getDay()]}</div>
                          <div className={`text-xs font-medium ${isToday ? "text-blue-600 dark:text-blue-400 font-bold" : "text-slate-700 dark:text-slate-200"}`}>{day.getDate()}</div>
                          {day.getDate() === 1 && <div className="text-[8px] text-slate-400 uppercase">{monthLabels[day.getMonth()]}</div>}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {vehicles.length === 0 ? (
                    <tr><td colSpan={DAYS_TO_SHOW + 1} className="text-center py-12 text-slate-400 text-sm">Nessun veicolo. Aggiungi veicoli dalla sezione Flotta.</td></tr>
                  ) : (
                    vehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="group" data-testid={`calendar-row-${vehicle.id}`}>
                        <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 px-3 py-2 transition-colors">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-slate-100 dark:bg-slate-700 rounded-md flex items-center justify-center shrink-0">
                              <Car className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{vehicle.brand} {vehicle.model}</p>
                              <p className="text-[11px] font-mono text-slate-400 tracking-wider">{vehicle.plate}</p>
                            </div>
                          </div>
                        </td>
                        {days.map((day) => {
                          const dateStr = formatDateISO(day);
                          const isToday = dateStr === todayStr;
                          const cell = getCellStatus(vehicle.id, dateStr, vehicle.status);

                          let bgClass = "";
                          let dotColor = "";

                          if (cell.type === "disponibile") {
                            bgClass = "bg-emerald-500/20 dark:bg-emerald-500/30 hover:bg-emerald-500/40";
                            dotColor = "bg-emerald-500";
                          } else if (cell.type === "cambio") {
                            bgClass = "bg-orange-500/30 dark:bg-orange-500/40 hover:bg-orange-500/50";
                            dotColor = "bg-orange-500";
                          } else if (cell.type === "noleggiata") {
                            bgClass = "bg-rose-500/25 dark:bg-rose-500/35 hover:bg-rose-500/45";
                            dotColor = "bg-rose-500";
                          } else if (cell.type === "prenotazione") {
                            bgClass = "bg-blue-500/25 dark:bg-blue-500/35 hover:bg-blue-500/45";
                            dotColor = "bg-blue-500";
                          } else if (cell.type === "manutenzione") {
                            bgClass = "bg-slate-500/25 dark:bg-slate-500/35 hover:bg-slate-500/45";
                            dotColor = "bg-slate-500";
                          }

                          return (
                            <td key={dateStr} className={`border-b border-r border-slate-200 dark:border-slate-700/50 p-0.5 ${isToday ? "ring-2 ring-inset ring-blue-400 dark:ring-blue-500" : ""}`}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    className={`w-full h-9 rounded-md ${bgClass} flex items-center justify-center transition-colors cursor-default`}
                                    data-testid={`cell-${vehicle.id}-${dateStr}`}
                                    onClick={() => setSelectedCell(
                                      selectedCell?.vehicleId === vehicle.id && selectedCell?.date === dateStr
                                        ? null
                                        : { vehicleId: vehicle.id, date: dateStr, ...cell, vehicle }
                                    )}
                                  >
                                    <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 shadow-lg rounded-lg p-2.5 text-xs whitespace-pre-line max-w-[250px]">
                                  <p className="font-semibold text-slate-900 dark:text-slate-100">{vehicle.brand} {vehicle.model} <span className="font-mono text-slate-400 font-normal">{vehicle.plate}</span></p>
                                  {cell.type === "cambio" && cell.rentals ? (
                                    <div className="mt-1 space-y-2">
                                      <p className="font-medium text-orange-500">Cambio Cliente</p>
                                      {cell.rentals.map((cr, ci) => (
                                        <div key={ci} className="pl-2 border-l-2 border-orange-400 space-y-0.5">
                                          <p className="text-slate-700 dark:text-slate-300 font-medium">{cr.client?.name || ""} {cr.client?.surname || ""}</p>
                                          <p className="text-slate-500 dark:text-slate-400">{formatDate(cr.start_date)} {cr.start_time || ""} → {formatDate(cr.end_date)} {cr.end_time || ""}</p>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (cell.type === "noleggiata" || cell.type === "prenotazione") && cell.rental ? (
                                    <div className="mt-1 space-y-0.5">
                                      <p className={`font-medium ${cell.type === "prenotazione" ? "text-blue-500" : "text-rose-500"}`}>
                                        {cell.type === "prenotazione" ? "Prenotazione" : "Noleggio Attivo"}
                                      </p>
                                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-400">Cliente:</span> {cell.rental.client?.name || ""} {cell.rental.client?.surname || ""}</p>
                                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-400">Periodo:</span> {formatDate(cell.rental.start_date)} - {formatDate(cell.rental.end_date)}</p>
                                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-400">Orario:</span> {cell.rental.start_time || "09:00"} - {cell.rental.end_time || "09:00"}</p>
                                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-400">Totale:</span> <span className="font-semibold">{cell.rental.total_amount?.toFixed(2) || 0} EUR</span></p>
                                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-400">Contratto:</span> #{cell.rental.contract_number || "--"}</p>
                                    </div>
                                  ) : (
                                    <p className={`mt-1 font-medium ${cell.type === "manutenzione" ? "text-slate-500" : "text-emerald-500"}`}>
                                      {cell.type === "manutenzione" ? "In manutenzione" : "Disponibile"}
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Selected Cell Detail */}
        {selectedCell && (
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm rounded-xl animate-in fade-in slide-in-from-bottom-2 duration-200" data-testid="cell-detail-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    selectedCell.type === "disponibile" ? "bg-emerald-500/20" :
                    selectedCell.type === "cambio" ? "bg-orange-500/20" :
                    selectedCell.type === "prenotazione" ? "bg-blue-500/20" :
                    selectedCell.type === "noleggiata" ? "bg-rose-500/20" : "bg-slate-500/20"
                  }`}>
                    {selectedCell.type === "disponibile" && <CheckCircle2 className="w-5 h-5 text-emerald-500" />}
                    {selectedCell.type === "cambio" && <ArrowLeftRight className="w-5 h-5 text-orange-500" />}
                    {selectedCell.type === "prenotazione" && <Car className="w-5 h-5 text-blue-500" />}
                    {selectedCell.type === "noleggiata" && <XCircle className="w-5 h-5 text-rose-500" />}
                    {selectedCell.type === "manutenzione" && <Wrench className="w-5 h-5 text-slate-500" />}
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-slate-100">
                      {selectedCell.vehicle?.brand} {selectedCell.vehicle?.model}
                      <span className="ml-2 text-xs font-mono text-slate-400">{selectedCell.vehicle?.plate}</span>
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {new Date(selectedCell.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}
                    </p>
                  </div>
                </div>
                <Badge className={`text-xs border ${
                  selectedCell.type === "disponibile" ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border-emerald-300 dark:border-emerald-700" :
                  selectedCell.type === "cambio" ? "bg-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-300 dark:border-orange-700" :
                  selectedCell.type === "prenotazione" ? "bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-700" :
                  selectedCell.type === "noleggiata" ? "bg-rose-500/20 text-rose-600 dark:text-rose-400 border-rose-300 dark:border-rose-700" :
                  "bg-slate-500/20 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-600"
                }`}>
                  {selectedCell.type === "disponibile" ? "Disponibile" :
                   selectedCell.type === "cambio" ? "Cambio Cliente" :
                   selectedCell.type === "prenotazione" ? "Prenotazione" :
                   selectedCell.type === "noleggiata" ? "Noleggiata" : "Manutenzione"}
                </Badge>
              </div>
              {selectedCell.type === "cambio" && selectedCell.rentals ? (
                <div className="mt-3 space-y-2">
                  {selectedCell.rentals.map((cr, ci) => (
                    <div key={ci} className="p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg text-sm space-y-1 border-l-4 border-orange-400">
                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Cliente:</span> <span className="font-medium">{cr.client?.name} {cr.client?.surname}</span></p>
                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Periodo:</span> {formatDate(cr.start_date)} {cr.start_time || ""} → {formatDate(cr.end_date)} {cr.end_time || ""}</p>
                      <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Totale:</span> <span className="font-medium">{cr.total_amount?.toFixed(2)} EUR</span></p>
                    </div>
                  ))}
                </div>
              ) : selectedCell.rental && (
                <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm space-y-1">
                  <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Cliente:</span> <span className="font-medium">{selectedCell.rental.client?.name} {selectedCell.rental.client?.surname}</span></p>
                  <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Periodo:</span> {formatDate(selectedCell.rental.start_date)} → {formatDate(selectedCell.rental.end_date)}</p>
                  <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Orario:</span> {selectedCell.rental.start_time || "09:00"} → {selectedCell.rental.end_time || "09:00"}</p>
                  <p className="text-slate-700 dark:text-slate-300"><span className="text-slate-500 dark:text-slate-400">Totale:</span> <span className="font-medium">{selectedCell.rental.total_amount?.toFixed(2)} EUR</span></p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </TooltipProvider>
  );
}
